import { getHost } from '@/actions/instance';
import { staticFetch } from '@/api';
import KVStore from '@/storage/kv-store';
import { getClient } from '@/stores/auth';
import { useFrontendConfigStore } from '@/stores/frontend-config';
import { useSettingsStore } from '@/stores/settings';

import type { APIEntity } from '@/types/entities';

const rememberFrontendConfig = (host: string | null) =>
  KVStore.getItemOrError(`frontendConfig:${host}`)
    .then((frontendConfig) => {
      useSettingsStore.getState().actions.loadDefaultSettings(frontendConfig?.defaultSettings);
      useFrontendConfigStore.getState().actions.rememberConfig(frontendConfig);
      return true;
    })
    .catch(() => false);

/** Conditionally fetches Nicolium config depending on backend features */
const fetchFrontendConfig = async (host: string | null) => {
  const client = getClient();
  const features = client.features;

  if (features.frontendConfigurations) {
    const data = await client.instance.getFrontendConfigurations();
    const foundData = data['nicolium'] || data['pl_fe'];

    if (foundData) {
      importFrontendConfig(foundData, host);
      return foundData;
    }
  }
  return fetchFrontendConfigJson(host);
};

/** Loads the cached config before fetching the current config. */
const loadFrontendConfig = async () => {
  const host = getHost();

  await rememberFrontendConfig(host);
  return fetchFrontendConfig(host);
};

const fetchFrontendConfigJson = (host: string | null) =>
  staticFetch('/instance/nicolium.json', { cache: 'no-store' }, true)
    .then(({ json: data }) => {
      if (!isObject(data)) throw new Error('nicolium.json fetch failed');
      importFrontendConfig(data, host);
      return data;
    })
    .catch(() => {
      useFrontendConfigStore.getState().actions.configFetchFailed();
    });

const importFrontendConfig = (frontendConfig: APIEntity, host: string | null) => {
  if (!frontendConfig.brandColor) frontendConfig.brandColor = '#d80482';

  useSettingsStore.getState().actions.loadDefaultSettings(frontendConfig?.defaultSettings);
  useFrontendConfigStore.getState().actions.importConfig(frontendConfig, host || '');
};

// https://stackoverflow.com/a/46663081
const isObject = (o: any) => o instanceof Object && o.constructor === Object;

export { fetchFrontendConfig, loadFrontendConfig };
